import React, { useEffect, useState, useContext } from "react";
import CartHeader from "../Cart/Header";
import { FaRupeeSign, FaRegCreditCard, FaMoneyBillWave } from "react-icons/fa";
import { AiOutlineQrcode } from "react-icons/ai";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./PaymentScreen.css";

import { KioskAuthContext } from "../../../ContextAPI/KisokContext";

export default function Payment() {
  const { ownerId, token } = useContext(KioskAuthContext);

  const [selected, setSelected] = useState("cash");
  const [amount, setAmount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [cartItems, setCartItems] = useState([]);

  const navigate = useNavigate();

  // --------------------------------------------------------
  // Load Cart for selected owner
  // --------------------------------------------------------
  useEffect(() => {
    const fetchCart = async () => {

      console.log("===== FETCH CART DEBUG =====");
      console.log("OwnerId:", ownerId);
      console.log("Token:", token);

      if (!ownerId || !token) {
        alert("Owner not logged in. Please log in.");
        setLoading(false);
        return;
      }

      try {
        // ✅ USE ENVIRONMENT VARIABLE FOR LOCAL TESTING
        const API_URL = process.env.REACT_APP_API_URL || "https://fliplyn-b-c.fly.dev";
        const cartEndpoint = `${API_URL}/owner/cart/${ownerId}`;

        const res = await axios.get(
          cartEndpoint,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        console.log("Cart API response:", res.data);

        setAmount(res.data.final_total ?? res.data.total_amount ?? 0);
        setCartItems(Array.isArray(res.data.items) ? res.data.items : []);

      } catch (error) {
        console.error("Failed to fetch cart:", error);
        alert("Failed to load payment amount.");
      } finally {
        setLoading(false);
      }
    };

    fetchCart();
  }, [ownerId, token]);

  if (loading)
    return (
      <div className="pm-loader-wrapper">
        <div className="pm-loader"></div>
      </div>
    );

  // --------------------------------------------------------
  // CREATE CASH PAYMENT ORDER
  // --------------------------------------------------------
  const handleCashPayment = async () => {

    console.log("===== CASH ORDER DEBUG =====");
    console.log("Cart Items:", cartItems);

    if (!cartItems.length) {
      alert("Cart is empty.");
      return;
    }

    try {
      const orderPayload = {
        owner_id: ownerId,
        items: cartItems.map((item) => ({
          item_id: item.item_id,
          quantity: item.quantity,
        })),
        payment_method: "CASH",
      };

      console.log("Cash Order Payload:", orderPayload);

      // ✅ USE ENVIRONMENT VARIABLE FOR LOCAL TESTING
      const API_URL = process.env.REACT_APP_API_URL || "https://fliplyn-b-c.fly.dev";

      const res = await axios.post(
        `${API_URL}/orders/cash`,
        orderPayload
      );

      console.log("Cash order placed:", res.data);

      const orderId = res.data.order_id;

      const orderDetails = await axios.get(
        `${API_URL}/orders/${orderId}`
      );

      console.log("Full order details:", orderDetails.data);

      // 🔥 UPDATED PRINT (NO POPUP)
      try {
        if (window.Android && window.Android.print) {
          window.Android.print(JSON.stringify(orderDetails.data));
        } else {
          console.warn("Android print not available");
        }
      } catch (err) {
        console.error("Thermal print failed:", err);
      }

      navigate("/order-success", { state: orderDetails.data });

    } catch (err) {
      console.error("Cash order failed:", err);
      console.error("Backend response:", err?.response?.data);
      alert("Failed to place cash order");
    }
  };

  // --------------------------------------------------------
  // CREATE ONLINE ORDER (UPI)
  // --------------------------------------------------------
  const handleUPIPayment = async () => {

    console.log("===== UPI ORDER DEBUG =====");
    console.log("OwnerId:", ownerId);
    console.log("Cart Items:", cartItems);

    if (!cartItems.length) {
      alert("Cart is empty.");
      return;
    }

    try {
      const orderPayload = {
        owner_id: ownerId,
        items: cartItems.map((item) => ({
          item_id: item.item_id,
          quantity: item.quantity,
        })),
      };

      console.log("UPI Order Payload:", orderPayload);
      
      // ✅ USE ENVIRONMENT VARIABLE FOR LOCAL TESTING
      const API_URL = process.env.REACT_APP_API_URL || "https://fliplyn-b-c.fly.dev";
      const orderEndpoint = `${API_URL}/orders/`;
      
      console.log("API URL:", orderEndpoint);

      const orderRes = await axios.post(
        orderEndpoint,
        orderPayload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Order created:", orderRes.data);

      const backendOrderId = orderRes.data.order?.id;
      const totalAmount = orderRes.data.order?.total_amount;
      const paymentSessionId = orderRes.data.payment?.payment_session_id;
      const qrImage = orderRes.data.payment?.qr_image;

      console.log("Backend Order ID:", backendOrderId);
      console.log("Payment Session ID:", paymentSessionId);
      console.log("QR Image:", qrImage ? "QR RECEIVED" : "NO QR");

      navigate("/upi-payment", {
        state: {
          backendOrderId,
          totalAmount,
          paymentSessionId,
          qrImage,
        },
      });

    } catch (err) {
      console.error("===== ORDER CREATION FAILED =====");
      console.error("Axios Error:", err);
      console.error("Backend Response:", err?.response?.data);
      console.error("Backend Status:", err?.response?.status);
      console.error("Backend Headers:", err?.response?.headers);
      alert("Failed to create order");
    }
  };

  return (
    <div>
      <CartHeader />

      <div className="pm-page">
        <main className="pm-container">
          <h1 className="pm-title">Select Payment</h1>
          <p className="pm-subtitle">Choose how you'd like to pay for your order</p>

          <div className="pm-amount-card">
            <div className="pm-amount-left">
              <div className="pm-amount-icon">
                <FaRegCreditCard />
              </div>
              <div className="pm-amount-label">Amount to Pay</div>
            </div>

            <div className="pm-amount-value">
              <FaRupeeSign className="pm-rupee" />
              <span className="pm-amount-number">{amount}</span>
            </div>
          </div>

          <div className="pm-backdrop">
            <button
              className={`pm-option ${selected === "upi" ? "pm-option--active" : ""}`}
              onClick={() => setSelected("upi")}
            >
              <div className="pm-option-left">
                <div className="pm-option-logo">
                  <AiOutlineQrcode />
                </div>
                <div className="pm-option-texts">
                  <div className="pm-option-title">Pay via UPI</div>
                  <div className="pm-option-desc">
                    Scan with GPay / PhonePe / Paytm
                  </div>
                </div>
              </div>

              <div className="pm-option-radio">
                <span className={`pm-radio ${selected === "upi" ? "pm-radio--checked" : ""}`}>
                  {selected === "upi" && <span className="pm-radio-dot" />}
                </span>
              </div>
            </button>

            <button
              className={`pm-option ${selected === "cash" ? "pm-option--active pm-option--accent" : ""}`}
              onClick={() => setSelected("cash")}
            >
              <div className="pm-option-left">
                <div className="pm-option-logo pm-option-logo--cash">
                  <FaMoneyBillWave />
                </div>

                {/* <div className="pm-option-texts">
                  <div className="pm-option-title">Pay via Cash</div>
                  <div className="pm-option-desc">
                    Pay at the kiosk counter
                  </div>
                </div> */}
              </div>

              <div className="pm-option-radio">
                <span className={`pm-radio ${selected === "cash" ? "pm-radio--checked" : ""}`}>
                  {selected === "cash" && <span className="pm-radio-dot" />}
                </span>
              </div>
            </button>
          </div>

          <p className="pm-note">
            For UPI, QR will be shown on the next screen.
          </p>

          <div className="pm-bottom">
            <button
              className="pm-continue-btn"
              onClick={() => {
                if (selected === "upi") handleUPIPayment();
                else handleCashPayment();
              }}
            >
              Continue <span className="pm-continue-arrow">→</span>
            </button>

            <p className="pm-help">Need assistance? Ask staff</p>
          </div>
        </main>
      </div>
    </div>
  );
}